<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">

          <BackButton
              :label="$t('general.button.back')"
              :to="{ name: 'clientList' }"
              marginBottom="5"/>

          <div class="d-flex justify-content-between">
            <h6 class="element-header">
              {{ $t('client.title') }}
            </h6>
          </div>

          <div class="element-box">
            <form @submit.prevent="submitForm" novalidate>
              <div class="row">
                <div class="col-lg-4 col-md-6">
                  <BaseInput
                      v-model="formData.customerNumber"
                      label="Kundennummer"
                      readonly/>
                  <BaseInput
                      v-model="formData.registrationOffice"
                      label="Abrechnungsstelle"
                      :required=true
                      :validation="v$.registrationOffice"
                      :messages="v$.registrationOffice.required.$message"/>
                  <BaseInput
                      v-model="formData.name"
                      label="Kostenträger"
                      :required=true
                      :disabled="formData.hubspotId"
                      :validation="v$.name"
                      :messages="v$.name.required.$message"/>
                  <BaseInput
                      v-model="formData.forAttention"
                      label="Z Hd."/>
                  <BaseInput
                      v-model="formData.payerIk"
                      label="Kostenträger IK"/>
                </div>

                <div class="col-lg-4 col-md-6">
                  <BaseInput
                      v-model="formData.addressFirst"
                      label="Adresszeile 1"
                      :required=true
                      :disabled="formData.hubspotId"
                      :validation="v$.addressFirst"
                      :messages="v$.addressFirst.required.$message"/>
                  <BaseInput
                      v-model="formData.addressSecond"
                      label="Adresszeile 2"/>
                  <BaseInput
                      v-model="formData.zip"
                      label="PLZ"
                      :required=true
                      :disabled="formData.hubspotId"
                      :validation="v$.zip"
                      :messages="v$.zip.required.$message"/>
                  <BaseInput
                      v-model="formData.city"
                      label="Ort"
                      :required=true
                      :disabled="formData.hubspotId"
                      :validation="v$.city"
                      :messages="v$.city.required.$message"/>
                  <BaseInput
                      v-model="formData.phone"
                      :disabled="formData.hubspotId"
                      label="Telefonnummer"/>
                </div>

                <div class="col-lg-4 col-md-12">
                  <BaseInput
                      v-model="formData.email"
                      type="email"
                      label="Email"
                      :validation="v$.email"
                      :disabled="formData.hubspotId"
                      :messages="v$.email.isValidEmail.$message"/>
                  <BaseInput
                      v-model="formData.debtor"
                      type="number"
                      label="Debitor"/>
                  <BaseInput
                      v-model="formData.pauschal"
                      type="number"
                      label="Pauschalabrechnung (%)"/>
                  <div v-if="billingTypeDropdown && billingTypeDropdown.length">
                    <SelectedDropdown
                        v-model="formData.billingType"
                        :options="billingTypeDropdown"
                        label="Art der Abrechnung"
                        placeholder="Wählen Sie die Art der Abrechnung aus"/>
                  </div>
                  <div class="mt-5">
                    <BaseCheckbox
                        v-model="formData.unitsReduction"
                        label="Reduzierung der UE bei UN"
                        labelPosition="right"/>
                  </div>
                </div>
              </div>

              <div class="row" v-if="companyKey === 'BSG'">
                <div class="col-12">
                  <div v-if="officeDropdown && officeDropdown.length">
                    <SelectVue
                        v-model="formData.officeIds"
                        :options="officeDropdown"
                        :is-clear="true"
                        :is-multi="true"
                        :required="true"
                        label="Standort"
                        :validation="v$.officeIds"
                        :messages="v$.officeIds.required.$message"/>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <TextEditor
                      v-model="formData.specialFeatures"
                      @update:model-value="formData.specialFeatures = $event"
                      :inputStyle="{ height: '150px' }"
                      label="Besonderheiten intern"/>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-12">
                  <TextEditor
                      v-model="formData.specialFeaturesForCoach"
                      @update:model-value="formData.specialFeaturesForCoach = $event"
                      :inputStyle="{ height: '150px' }"
                      label="Besonderheiten für Coaches"/>
                </div>
              </div>

              <div class="form-buttons-w text-center mt-4">
                <SaveButton
                    :label="$t('general.button.save')"
                    :loading="isSubmitting"/>
              </div>
            </form>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import clientService from '@/api/Client';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import SelectedDropdown from '@/components/SelectDropdown.vue';
import SelectVue from '@/components/SelectVue.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
// @ts-ignore
import {getOfficeDropdown} from '@/shared/dropdowns';
import {isValidEmail, requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import useVuelidate from '@vuelidate/core';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {IClient, IEnums, ILocation} from "@/interfaces/Client/IClient";
import TextEditor from "@/components/TextEditor.vue";
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";
import {piniaCompanySettingsStore} from "@/store/piniaCompanySettingsStore";

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed<IEnums>(() => piniaEnumsState.enums as unknown as IEnums);
const piniaCompanySettingsState = piniaCompanySettingsStore()
const companySettings = computed(() => piniaCompanySettingsState.companySettings)
const companyKey = companySettings.value?.companyKey;

const {t} = useI18n();
const billingTypeDropdown = ref<string[]>([])
const officeDropdown = ref([]);
const isSubmitting = ref(false);
const route = useRoute();
const clientId = route.params.id;

const formData = ref<IClient>({
  customerNumber: '',
  registrationOffice: '',
  name: '',
  forAttention: '',
  addressFirst: '',
  addressSecond: '',
  payerIk: '',
  billingType: '',
  city: '',
  zip: '',
  phone: '',
  email: '',
  pauschal: 0,
  debtor: '',
  officeIds: [],
  specialFeatures: '',
  specialFeaturesForCoach: '',
  unitsReduction: false
});

const rules = computed(() => {
  return {
    registrationOffice: requiredRule(t),
    name: requiredRule(t),
    addressFirst: requiredRule(t),
    city: requiredRule(t),
    zip: requiredRule(t),
    email: isValidEmail(t),
    officeIds: companyKey === 'BSG' ? requiredRule(t) : '',
  };
});

const v$ = useVuelidate<IClient>(rules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  // Set billingType to null if it's an empty string
  if (formData.value.billingType === '') {
    formData.value.billingType = null;
  }

  // Convert numeric fields from string to number
  formData.value.pauschal = Number(formData.value.pauschal); // or parseFloat if you prefer

  try {

    clientId ?
        await clientService.putClient(formData.value, +clientId) :
        await clientService.postClient(formData.value)

    await router.push({name: 'clientList'});
    isSubmitting.value = false;

    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getClient() {
  try {
    const {data} = await clientService.getClient(+clientId);
    const officeIds = (data.data.locations as ILocation[] || [])
        .map((loc) => loc.id)
        .filter((id): id is number => id != null);

    formData.value = {...data.data, officeIds: officeIds};
    formData.value.debtor = data.data?.debtor || '';
    formData.value.billingType = formData.value.billingType ?? '';
  } catch (error) {
    await ErrorToast(error);
  }
}

async function nextClientNumber() {
  try {
    const {data} = await clientService.getClientNextNumber();
    formData.value.customerNumber = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getPrivateCoachingEnumBillingTypes() {
  billingTypeDropdown.value = getEnums?.value?.privateCoachingBillingTypes || [];
}

onMounted(async () => {
  CloseToast();

  await getOfficeDropdown(officeDropdown);
  await getPrivateCoachingEnumBillingTypes();

  if (clientId) {
    await getClient();
  } else {
    await nextClientNumber();
  }
});

</script>

<style scoped>
</style>